import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useAppSelector } from 'app/hooks'
import {
  CBVSupportedViewType,
  IColorByValueConfig
} from 'app/services/api/colorByValue'
import { useGetEventTablesForDataViewQuery } from 'app/services/api/datamanagement/eventTableAttributes'
import { selectSystemScopeIdContainer } from 'app/layout/Navbar/Filters/globalFilterSelectors'
import OverlayScrollbar from 'common/components/OverlayScrollBar'
import LRSWrapper from 'features/DataManagement/Components/LinearReferenceGraphs/LRSWrapper'
import MapWrapper from 'features/DataManagement/Components/MapWrapper'
import DataTable, {
  IDataTableFilterModel
} from 'features/DataManagement/Components/Tables/DataTable'
import { IDataViewProps } from 'features/DataManagement/DataView'
import {
  RightSideViewAvailability,
  RightSideViewSelection
} from 'features/DataManagement/DataView/Details'

import Header from './Header'

const DefaultAttributeSetView = ({ dvBasePath }: IDataViewProps) => {
  const { attributeSetId } = useParams<{ attributeSetId: string }>()

  const systemScopeIdContainer = useAppSelector(selectSystemScopeIdContainer)

  const { currentEventTable } = useGetEventTablesForDataViewQuery(undefined, {
    selectFromResult: ({ data }) => ({
      currentEventTable: data?.find(
        (eventTable) => eventTable.id.toString() === attributeSetId?.toString()
      )
    })
  })

  const [colorByValueConfig, setColorByValueConfig] =
    useState<IColorByValueConfig>()

  const [selectedView, setSelectedView] =
    useState<RightSideViewSelection>('none')

  const [appliedTableFilters, setAppliedTableFilters] =
    useState<IDataTableFilterModel>({})

  useEffect(() => {
    if (currentEventTable != null) {
      if (currentEventTable.arcGisUrl != null) {
        setSelectedView('map')
      }

      /**
       * NOTE: CBV Config is initially used to pass down view meta to the
       *       CBV Config initializer/server side data binder
       */
      setColorByValueConfig({
        loadStatus: 'init',
        viewType: CBVSupportedViewType.dataManagementDefaultAttributesView,
        viewId: currentEventTable.id.toString(),
        viewName: currentEventTable.name,
        selectedAttributeId: null,
        attributesMap: []
      })
    }
  }, [currentEventTable])

  let availableSideViews: RightSideViewAvailability = 'none'

  if (currentEventTable?.typeCode != null) {
    if (currentEventTable.typeCode === 'Point') {
      availableSideViews = 'map'
    } else {
      // show all for 'Linear' or any other types that might
      // show up in the future..
      availableSideViews = 'all'
    }
  }

  return (
    <div className='flex flex-1 flex-col'>
      <Header
        dvBasePath={dvBasePath}
        viewName={currentEventTable?.name ?? ''}
        availableSideViews={availableSideViews}
        selectedSideView={selectedView}
        colorByValueConfig={colorByValueConfig}
        setSelectedView={setSelectedView}
        setColorByValueConfig={setColorByValueConfig}
      />
      <div className='flex flex-1 overflow-auto'>
        <div className='flex flex-2'>
          <DataTable
            // force remount on system scope change
            key={systemScopeIdContainer.updatedAt}
            systemScopeId={systemScopeIdContainer.id}
            systemScopeUpdatedAt={systemScopeIdContainer.updatedAt}
            tableType='default'
            // REVIEW: Entity for default attributes, what will it be for custom view?
            tableId={currentEventTable?.id.toString()}
            colorByValueConfig={colorByValueConfig}
            onFilterChangeCB={setAppliedTableFilters}
          />
        </div>
        {selectedView !== 'none' && (
          <div className='flex flex-1 flex-col'>
            {['map', 'split'].includes(selectedView) && (
              <div className='flex-1'>
                <MapWrapper
                  currentEventTable={currentEventTable}
                  colorByValue={colorByValueConfig}
                  appliedTableFilters={appliedTableFilters}
                />
              </div>
            )}
            {['charts', 'split'].includes(selectedView) && (
              <div className='flex flex-1 overflow-auto bg-gray-200'>
                <OverlayScrollbar>
                  <LRSWrapper
                    baselineId='5'
                    tableId={currentEventTable?.id.toString()}
                    appliedTableFilters={appliedTableFilters}
                  />
                </OverlayScrollbar>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default DefaultAttributeSetView
